import { LastWatched } from '../../lastWatched';
import { Channel, ChannelAsset } from '../types';

import { PersonalLineupPersonalization } from './types';
import { createNewChannelsPclDictionary, tryCreateChannel } from './utils';

type ComposePersonalLineupArgs = {
  channelAssetsMap: Map<number, ChannelAsset>;
  channelCollection: Array<number>;
  entitledChannels: Set<number>;
  lastWatched?: LastWatched;
  personalLineup: Pick<
    NonNullable<PersonalLineupPersonalization['personalLineup']>,
    'dictionary' | 'entries' | 'highestChannelNumber'
  >;
};

// Performance-wise we would like to create the most efficient algorithm with the minimun amount of iterations.
// During the iteration we need to find all new channels which don't present in PCL
// and derive the highest LCN in the collection so all new channels will be given with the greatest LCNs.
export function composePersonalLineup({
  channelAssetsMap,
  channelCollection,
  entitledChannels,
  lastWatched,
  personalLineup,
}: ComposePersonalLineupArgs) {
  const { dictionary: pclChannels, entries } = personalLineup;

  const channels: Array<Channel> = [];
  const newChannels: Array<number> = [];
  const oclChannels = new Set(channelCollection);

  const newChannelsPclDictionary = createNewChannelsPclDictionary(
    channelAssetsMap,
    personalLineup.dictionary,
    personalLineup.highestChannelNumber,
  );

  // operator can add more channels to the collection, user can by a package
  // so the OCL will be longer than PCL or remove channels
  // so the OCL will be shorter then PCL
  const totalChannelsCount = Math.max(channelCollection.length, entries.length);

  if (lastWatched?.channelId) {
    const channel = tryCreateChannel({ channelAssetsMap, entitledChannels, id: lastWatched?.channelId });

    if (channel) {
      channel.virtualNumber = pclChannels.get(channel.id) ?? newChannelsPclDictionary.get(channel.id);

      channels.push(channel);
    }
  }

  for (let i = 0; i < totalChannelsCount; i++) {
    // we need to track all new channels and add them later
    if (newChannelsPclDictionary.has(channelCollection[i])) {
      newChannels.push(channelCollection[i]);
    }

    // when OCL is longer than PCL index could be out of range
    const pclChannel = entries[i];

    if (!pclChannel) {
      continue;
    }

    // channel is no longer on collection
    if (!oclChannels.has(pclChannel.channelId)) {
      continue;
    }

    // need to skip it here, because it's already the first
    if (lastWatched?.channelId === pclChannel.channelId) {
      continue;
    }

    const channel = tryCreateChannel({ channelAssetsMap, entitledChannels, id: pclChannel.channelId });

    if (!channel) {
      continue;
    }

    channel.virtualNumber = pclChannel.channelNumber;

    channels.push(channel);
  }

  // add new channels to the end
  for (let i = 0; i < newChannels.length; i++) {
    const newChannelId = newChannels[i];

    // if last watch is part of new channels need to skip it here, because it's already the first
    if (lastWatched?.channelId === newChannelId) {
      continue;
    }

    const channel = tryCreateChannel({ channelAssetsMap, entitledChannels, id: newChannelId });

    if (!channel) {
      continue;
    }

    channel.virtualNumber = newChannelsPclDictionary.get(newChannelId);

    channels.push(channel);
  }

  return channels;
}
